import React, { useState, useEffect, useMemo } from 'react';
import {
    Form,
    Container,
    Row,
    Col,
    Card,
    Button,
} from 'react-bootstrap';
import {
    FaRegClock,
    FaAngleLeft,
    FaAngleRight,
    FaCoffee
} from 'react-icons/fa';

import { useSelector, useDispatch } from 'react-redux';
import { fetchUserPresences } from '../../../redux/slices/presenceSlice';
import { fetchHolidays } from '../../../redux/slices/absenceSlice';

import HoursModal from '../HoursModal/HoursModal';

const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth() + 1;

export const getLocalDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
};

export const getDateKeyFromValue = (value) => {
    if (!value) return null;

    if (typeof value === 'string') {
        const dateMatch = value.match(/^(\d{4}-\d{2}-\d{2})/);
        if (dateMatch) return dateMatch[1];
    }

    const date = new Date(value);
    return isNaN(date.getTime()) ? null : getLocalDateKey(date);
};

export const generateDaysOfMonth = (year, monthNumber) => {
    const monthIndex = monthNumber - 1;
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

    return Array.from({ length: daysInMonth }, (_, index) => new Date(year, monthIndex, index + 1));
};

export const formatAnnualLeaveDate = (date) => {
    const day = date.toLocaleDateString('pt-BR', { day: 'numeric' });
    const month = date.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
    const weekday = date.toLocaleDateString('pt-BR', { weekday: 'long' });

    return { day, month, weekday };
};

export const getDayType = (date, holidayMap = new Map(), hasPresence = false) => {
    const dayOfWeek = date.getDay();
    const dateKey = getLocalDateKey(date);

    if (holidayMap.has(dateKey)) return 'holiday';
    if (dayOfWeek === 0 || dayOfWeek === 6) return 'weekend';
    if (hasPresence) return 'worked';

    return 'business';
};

const getDayRowStyle = (dayType) => {
    const colors = {
        business: '#17296B',
        worked: '#123C69',
        weekend: '#273761',
        holiday: '#4B376F',
    };

    return {
        backgroundColor: colors[dayType] || colors.business,
        marginTop: '0.4%',
    };
};

const AnnualLeaveAdmin = () => {
    const dispatch = useDispatch();
    const user = useSelector(state => state.auth.user);
    const presences = useSelector(state => state.presences.items);
    const holidays = useSelector(state => state.absences?.holidays || []);

    const [selectedDay, setSelectedDate] = useState(null);
    const [presenceDays, setPresenceDays] = useState([]);
    const [totalHours, setTotalHours] = useState(0);
    const [totalExtraHours, setTotalExtraHours] = useState(0);

    const [selectedMonth, setSelectedMonth] = useState(CURRENT_MONTH);
    const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);

    const refreshPresences = () => {
        if (user && user.id) {
            dispatch(fetchUserPresences(user.id));
        }
    };

    useEffect(() => {
        if (user && user.id) {
            dispatch(fetchUserPresences(user.id));
        }
    }, [user, dispatch]);

    useEffect(() => {
        dispatch(fetchHolidays({ year: selectedYear, userId: user?.id }));
    }, [dispatch, selectedYear, user?.id]);

    const holidayMap = useMemo(() => new Map(
        holidays
            .map(holiday => {
                const key = getDateKeyFromValue(holiday.date);
                return key ? [key, holiday] : null;
            })
            .filter(Boolean)
    ), [holidays]);

    useEffect(() => {
        const allDays = generateDaysOfMonth(selectedYear, selectedMonth);

        const presenceMap = new Map();
        if (presences && presences.length > 0) {
            presences.forEach(presence => {
                const presenceDateRaw = presence.check_in;
                const presenceDate = presenceDateRaw ? new Date(presenceDateRaw) : null;
                if (!presenceDate || isNaN(presenceDate.getTime())) return;
                const dayKey = getLocalDateKey(presenceDate);
                if (!presenceMap.has(dayKey)) {
                    presenceMap.set(dayKey, []);
                }
                presenceMap.get(dayKey).push(presence);
            });
        }

        const daysArray = allDays.map(day => {
            const dayKey = getLocalDateKey(day);
            const dayPresences = presenceMap.get(dayKey) || [];

            let worked_hours = 0;
            dayPresences.forEach(presence => {
                if (presence.check_in && presence.check_out) {
                    const checkInTime = new Date(presence.check_in).getTime();
                    const checkOutTime = new Date(presence.check_out).getTime();
                    let totalPauseMs = 0;
                    if (presence.pauses && presence.pauses.length > 0) {
                        presence.pauses.forEach(pause => {
                            if (pause.start && pause.end) {
                                const pauseStart = new Date(pause.start).getTime();
                                const pauseEnd = new Date(pause.end).getTime();
                                if (!isNaN(pauseStart) && !isNaN(pauseEnd) && pauseEnd > pauseStart) {
                                    totalPauseMs += (pauseEnd - pauseStart);
                                }
                            }
                        });
                    }
                    let hours = (checkOutTime - checkInTime - totalPauseMs) / (1000 * 60 * 60);
                    if (hours < 0) hours = 0;
                    worked_hours += hours;
                }
            });

            return {
                day,
                worked_hours,
                extra_hours: worked_hours > 8 ? worked_hours - 8 : 0,
                dayType: getDayType(day, holidayMap, worked_hours > 0),
                shifts: []
            };
        });

        const totalHrs = daysArray.reduce((acc, day) => acc + day.worked_hours, 0);
        const totalExtraHrs = daysArray.reduce((acc, day) => acc + day.extra_hours, 0);

        setPresenceDays(daysArray);
        setTotalHours(totalHrs);
        setTotalExtraHours(totalExtraHrs);
    }, [presences, selectedMonth, selectedYear, holidayMap]);

    const handleClose = () => setSelectedDate(null);

    const handleViewDate = (dateObj) => {
        setSelectedDate(dateObj);
    };

    const handleGoToToday = () => {
        const today = new Date();
        const month = today.getMonth() + 1;
        const year = today.getFullYear();
        if (selectedMonth !== month || selectedYear !== year) {
            setSelectedMonth(month);
            setSelectedYear(year);
        }
    };

    const today = new Date();
    const isCurrentMonth = selectedMonth === (today.getMonth() + 1) && selectedYear === today.getFullYear();
    const recentDays = presenceDays
        .filter(item => isCurrentMonth ? item.day <= today : true)
        .sort((a, b) => b.day.getTime() - a.day.getTime());

    const FormatDate = formatAnnualLeaveDate;

    const FormatHours = (hours) => {
        let totalMinutes = Math.round(hours * 60);
        let hoursPart = Math.floor(totalMinutes / 60);
        let minutesPart = totalMinutes % 60;

        return `${hoursPart}h${minutesPart.toString().padStart(2, '0')}m`;
    };

    const FormatHoursForCards = (hours) => {
        let totalMinutes = Math.round(hours * 60);
        let hoursPart = Math.floor(totalMinutes / 60);
        let minutesPart = totalMinutes % 60;

        return `${hoursPart}:${minutesPart.toString().padStart(2, '0')}`;
    };

    const FormatHoursSign = (hours) => {
        if (hours < 0) {
            return `-${FormatHours(hours)}`;
        } else {
            return `+${FormatHours(hours)}`;
        }
    };

    const handleMonthChange = (e) => setSelectedMonth(Number(e.target.value));
    const handleYearChange = (e) => setSelectedYear(Number(e.target.value));

    return (
        <Container fluid style={{ background: "linear-gradient(to bottom, #0080ff, #2e005e)", minHeight: "100vh", overflow: 'auto' }}>
            <style>
                {`
                .accordion {
                    --bs-accordion-active-bg: #ffffff;
                    --bs-accordion-active-color: #000000;
                    --bs-accordion-btn-focus-box-shadow: none;
                }
                .accordion-button.collapsed {
                    background-color: #2B2B2B;
                    color: white;
                }

                .roundtop {
                    border-top-left-radius: 20px;
                    border-top-right-radius: 20px;
                }

                .roundbottom {
                    border-bottom-left-radius: 20px;
                    border-bottom-right-radius: 20px;
                }

                .custom-select {
                    appearance: none;
                    -webkit-appearance: none;
                    -moz-appearance: none;
                    background-color: #17296B;
                    color: white;
                    border: none;
                    padding-right: 25px;
                    background-image: url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white' width='18px' height='18px'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
                    background-repeat: no-repeat;
                    background-position: right 5px center;
                    background-size: 18px;
                }
            `}
            </style>
            <div style={{ height: '100%' }}>
                <Row>
                    <Col className='text-center text-white'>
                        <Button variant='dark' className='text-white text-center px-5 mt-3 fs-4 fw-bold' style={{ backgroundColor: "#17296B", borderRadius: "10px", cursor: "default" }}>Banco de Horas</Button>
                        <Card className='text-center rounded-4 shadow-lg text-white border-0 w-100' style={{ backgroundColor: '#17296B' }}>
                            <Card.Body className='p-0'>
                                <Row className='m-0 my-2'>
                                    <Col xs={6} className='d-flex justify-content-center align-items-center'>
                                        <div>
                                            <h4 className='fs-1 mb-0 fw-bold'>{FormatHours(totalHours)}</h4>
                                            <h4 style={{ fontSize: "16px" }} className='mt-1 mb-1 text-truncate'>Horas Trabalhadas</h4>
                                        </div>
                                    </Col>
                                    <Col xs={6} className='d-flex justify-content-center align-items-center'>
                                        <div>
                                            <h4 className='fs-1 mb-0 fw-bold'>{FormatHoursSign(totalExtraHours)}</h4>
                                            <h4 style={{ fontSize: "16px" }} className='mt-1 mb-1 text-truncate'>Saldo de Horas</h4>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>
            <Row>
                <Col xs={12} className='d-flex justify-content-between align-items-center mb-2 mt-1'>
                    <div className='d-flex justify-content-start align-items-center'>
                        <FaAngleLeft
                            className="fs-1 text-white mx-2"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                                if (selectedMonth === 1) {
                                    setSelectedMonth(12);
                                    setSelectedYear(selectedYear - 1);
                                } else {
                                    setSelectedMonth(selectedMonth - 1);
                                }
                            }}
                        />
                    </div>
                    <div className='d-flex gap-2 justify-content-between' style={{ height: "40px" }}>
                        <Button
                            variant='dark'
                            className='text-white border-0 text-center'
                            style={{ backgroundColor: "#17296B", borderRadius: "", cursor: "pointer" }}
                            onClick={handleGoToToday}
                            disabled={selectedMonth === (new Date().getMonth() + 1) && selectedYear === new Date().getFullYear()}
                        >
                            Hoje
                        </Button>
                        <Form.Select className="custom-select" value={selectedMonth} onChange={handleMonthChange}>
                            <option value="1">Janeiro</option>
                            <option value="2">Fevereiro</option>
                            <option value="3">Março</option>
                            <option value="4">Abril</option>
                            <option value="5">Maio</option>
                            <option value="6">Junho</option>
                            <option value="7">Julho</option>
                            <option value="8">Agosto</option>
                            <option value="9">Setembro</option>
                            <option value="10">Outubro</option>
                            <option value="11">Novembro</option>
                            <option value="12">Dezembro</option>
                        </Form.Select>

                        <Form.Select className="custom-select" value={selectedYear} onChange={handleYearChange}>
                            <option value="2025">2025</option>
                            <option value="2024">2024</option>
                            <option value="2023">2023</option>
                        </Form.Select>
                    </div>
                    <div className='d-flex justify-content-end align-items-center'>
                        <FaAngleRight
                            className="fs-1 text-white mx-2"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                                if (selectedMonth === 12) {
                                    setSelectedMonth(1);
                                    setSelectedYear(selectedYear + 1);
                                } else {
                                    setSelectedMonth(selectedMonth + 1);
                                }
                            }}
                        />
                    </div>
                </Col>
            </Row>
            <Row>
                <div>
                    <Container fluid className='w-100 bg-transparent' style={{ height: "50px" }}>
                        <Row
                            className=" text-white py-3 fs-4 roundtop"
                            style={{ backgroundColor: '#17296B' }}
                        >
                            <Col className="text-start fw-bold ms-2">Data</Col>
                            <Col className="text-center fw-bold">Horas</Col>
                            <Col className="text-end fw-bold me-2">Ações</Col>
                        </Row>
                        <Row>
                            <Col xs={12} className='mb-4' style={{ overflow: 'auto', height: '70%' }}>
                                {recentDays.map((item, index) => (
                                    <Row
                                        key={getLocalDateKey(item.day)}
                                        style={getDayRowStyle(item.dayType)}
                                        className={`text-white py-1 annual-leave-day-row annual-leave-${item.dayType} ${index === recentDays.length - 1 ? 'roundbottom' : ''}`}
                                    >
                                        <Col xs={12}>
                                            <div style={{ paddingLeft: "5px" }}>
                                                <div className='d-flex align-items-center'>
                                                    <div className='w-100 mt-2'>
                                                        <span className='fs-3 fw-semibold'>{FormatDate(item.day).day} -</span>
                                                        <span className='opacity-100 ms-2 fs-6 fw-medium'>{FormatDate(item.day).month}.</span>
                                                        <p>
                                                            <span className='opacity-75 fs-6'>{FormatDate(item.day).weekday}</span>
                                                        </p>
                                                    </div>
                                                    <div className={`position-relative rounded-4 d-flex align-items-center`} style={{ padding: "13px" }}>
                                                        {item.worked_hours > 0 ? (
                                                            <>
                                                                <FaRegClock className='me-2 fs-5 text-white' />
                                                                <span className='fs-5 fw-medium text-white'>{FormatHoursForCards(item.worked_hours)}</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FaCoffee className='me-2 fs-5 text-white' />
                                                                <span className='fs-5 fw-medium text-white'>Descanso</span>
                                                            </>
                                                        )}
                                                    </div>
                                                    <div className='d-flex justify-content-end align-items-center w-100 ms-3'>
                                                        <div className='d-flex justify-content-end align-items-center' style={{ cursor: "pointer" }} onClick={() => handleViewDate(item.day)}>
                                                            <FaAngleRight className='fs-1 opacity-75 me-2 my-auto' />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                ))}
                            </Col>
                        </Row>
                    </Container>
                </div>
            </Row>
            <Row className='overflow-auto' style={{ scrollbarColor: '#ffffff rgba(0, 0, 0, 0)', scrollbarWidth: 'thin', scrollbarSize: 20 }}>
                <div className='mt-5' />
            </Row>

            {
                selectedDay && (
                    <HoursModal
                        selectedDay={selectedDay}
                        handleClose={handleClose}
                        handleViewDate={handleViewDate}
                        presencesForDay={
                            presences.filter(p => {
                                const d = new Date(p.check_in);
                                return d.getFullYear() === selectedDay.getFullYear() &&
                                    d.getMonth() === selectedDay.getMonth() &&
                                    d.getDate() === selectedDay.getDate();
                            })
                        }
                        FormatHours={FormatHours}
                        refreshPresences={refreshPresences}
                    />
                )
            }
        </Container >
    );
};

export default AnnualLeaveAdmin;
